import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import SlideOver from "../../Modal/SlideOver";
import {
  ArrowSmRightIcon,
  ArrowSmUpIcon,
  PencilIcon,
} from "@heroicons/react/outline";
import {
  currencyFormat,
  convertToTitleCase,
  formatDateTime,
} from "../../../../Utils";
import { TRANSACTION_TYPES } from "../../../../Constants";
import { NumericFormat } from "react-number-format";
import { useAuthState, usePeriodLock } from "../../../../Context";
import PeriodLockCell from "../PeriodLockCell";
import {
  getPayrollJournalPeriod,
  isPayrollJournal,
} from "../../../../Utils/payroll";
import { isStockAdjustmentJournal } from "../../../../Utils/AccountingUtil";
import { isTransactionPaid } from "../../../../Utils/PaymentUtils";

const paymentTransactionTypes = ["PAYABLE_PAYMENT", "RECEIVABLE_PAYMENT"];

const getTransactionTypeInfo = (type) => {
  const transactionType =
    TRANSACTION_TYPES.find((t) => t.value === type) ||
    TRANSACTION_TYPES.find((t) => t.value === "OTHER");
  return {
    label: convertToTitleCase(transactionType.value),
    ...transactionType.style,
  };
};

const TransactionDetailModal = ({
  open,
  onClose,
  onEdit,
  transaction,
  parentTransaction,
  parentTransactionLoading,
  onViewParentInvoice,
}) => {
  const { t, i18n } = useTranslation();
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  // Above the `!transaction` bail-out: hooks cannot sit behind an early return.
  const { user } = useAuthState();
  const { isDateClosed } = usePeriodLock(user?.mainBusiness?.id);

  if (!transaction) return null;

  const periodLocked = isDateClosed(transaction.createdAt);

  const typeInfo = getTransactionTypeInfo(transaction.type);
  const descriptionLength = transaction.description?.length || 0;
  const isLongDescription = descriptionLength > 100;

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title={t("transactions.detail.title") || "Transaction Details"}
      actionButton={
        // Swapped whole, not disabled: this header holds exactly one control, so
        // a greyed pencil would be the only thing there and would say nothing
        // about why. The lock states the reason and keeps reading unaffected.
        periodLocked ? (
          <PeriodLockCell />
        ) : onEdit && isStockAdjustmentJournal(transaction) ? (
          <PeriodLockCell label="accounting.stockAdjustmentJournal.lockedRow" />
        ) : onEdit && isPayrollJournal(transaction) ? (
          // The server refuses edits to a payroll journal; the pencil gives
          // way to the page that can change it - void or cancel the payment.
          <Link
            to={`/v2/accounting/payroll${
              getPayrollJournalPeriod(transaction)
                ? `?month=${getPayrollJournalPeriod(transaction)}`
                : ""
            }`}
            className="text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            {t("payroll.journal.openPayroll")}
          </Link>
        ) : (
          onEdit && (
            <button
              type="button"
              className="rounded-md bg-white text-gray-400 hover:text-gray-500"
              onClick={() => onEdit(transaction)}
            >
              <PencilIcon className="h-5 w-5" />
            </button>
          )
        )
      }
    >
      <div className="space-y-6 mb-6">
        {/* Related Invoice Card */}
        {paymentTransactionTypes.includes(transaction.type) &&
          parentTransactionLoading && (
            <div className="bg-white border border-gray-200 shadow-sm overflow-hidden rounded-lg animate-pulse">
              <div className="px-4 py-4 sm:px-6">
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-gray-200 rounded-lg w-9 h-9" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3.5 bg-gray-200 rounded w-1/3" />
                    <div className="h-3 bg-gray-200 rounded w-1/4" />
                  </div>
                  <div className="flex flex-col items-end space-y-2">
                    <div className="h-3.5 bg-gray-200 rounded w-20" />
                    <div className="h-3 bg-gray-200 rounded w-16" />
                  </div>
                </div>
              </div>
            </div>
          )}

        {paymentTransactionTypes.includes(transaction.type) &&
          !parentTransactionLoading &&
          parentTransaction?.id && (
            <div
              role="button"
              tabIndex={0}
              onClick={() =>
                onViewParentInvoice && onViewParentInvoice(parentTransaction)
              }
              onKeyDown={(e) =>
                e.key === "Enter" &&
                onViewParentInvoice &&
                onViewParentInvoice(parentTransaction)
              }
              className="block bg-white border border-gray-200 divide-y divide-gray-200 shadow-sm overflow-hidden rounded-lg cursor-pointer hover:border-blue-300 transition-colors duration-150"
            >
              <div className="px-4 py-4 sm:px-6">
                <div className="flex">
                  <div className="flex flex-1 gap-4">
                    <div className="flex items-center">
                      <div className="p-2 bg-amber-100 border border-amber-300 rounded-lg">
                        {["PAYABLE", "EXPENSE_PAYABLE"].includes(
                          parentTransaction.type,
                        ) ? (
                          <ArrowSmRightIcon className="w-5 h-5 text-amber-600 rotate-45" />
                        ) : (
                          <ArrowSmUpIcon className="w-5 h-5 text-amber-600 rotate-45" />
                        )}
                      </div>
                    </div>
                    <div className="flex items-start flex-col">
                      <p className="text-sm font-medium text-gray-900">
                        {t(convertToTitleCase(parentTransaction?.type))}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatDateTime(
                          parentTransaction.createdAt,
                          i18n.language,
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end">
                    <NumericFormat
                      className="text-sm font-medium text-gray-900"
                      displayType="text"
                      value={parentTransaction.total}
                      thousandSeparator="."
                      decimalSeparator=","
                      prefix="Rp"
                    />
                    {parentTransaction.invoiceNumber && (
                      <p className="text-xs text-gray-500">
                        Invoice #{parentTransaction.invoiceNumber}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

        {/* Header Summary */}
        <div className="bg-white px-4 py-5 shadow sm:rounded-lg sm:p-6 border border-gray-100">
          <div className="flex justify-between items-center mb-4">
            <div>
              <p className="text-sm font-medium text-gray-500">
                {t("accounting.dashboard.transaction.field.amount")}
              </p>
              <h3
                className={`mt-1 text-3xl font-semibold ${
                  typeInfo.textColorClass || "text-gray-900"
                }`}
              >
                {typeInfo.prefix}
                {currencyFormat(transaction.total)}
              </h3>
            </div>
            <span
              className={`inline-flex rounded-full px-3 py-1 text-sm font-semibold leading-5 ${typeInfo.colorClass}`}
            >
              {t(typeInfo.label)}
            </span>
          </div>

          <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2 mt-4 pt-4 border-t border-gray-200">
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("accounting.dashboard.transaction.field.date")}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {formatDateTime(transaction.createdAt, i18n.language)}
              </dd>
            </div>

            {transaction.paymentDueAt && (
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">
                  {t("transactions.fields.dueDate")}
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {dayjs(transaction.paymentDueAt)
                    .locale(i18n.language)
                    .format("DD MMM YYYY")}
                </dd>
              </div>
            )}

            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("transactions.fields.paymentStatus")}
              </dt>
              <dd className="mt-1 text-sm text-gray-900 capitalize">
                {isTransactionPaid(transaction)
                  ? t("transactions.statusPayment.paid")
                  : t("transactions.statusPayment.notPaid")}
              </dd>
            </div>

            {transaction.partner && (
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">
                  {t("transactions.fields.partner")
                    ? t("transactions.fields.partner")
                    : "Partner"}
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {transaction.partner.name}
                </dd>
              </div>
            )}

            <div className="sm:col-span-2">
              <dt className="text-sm font-medium text-gray-500">
                {t("accounting.dashboard.transaction.field.description")}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {transaction.description ? (
                  <>
                    <p
                      className={`whitespace-pre-wrap ${
                        !isDescriptionExpanded && isLongDescription
                          ? "line-clamp-2"
                          : ""
                      }`}
                    >
                      {transaction.description}
                    </p>
                    {isLongDescription && (
                      <button
                        type="button"
                        className="block mt-1 text-xs text-blue-600 hover:text-blue-500 font-medium text-left"
                        onClick={() =>
                          setIsDescriptionExpanded(!isDescriptionExpanded)
                        }
                      >
                        {isDescriptionExpanded
                          ? t("Show less")
                          : t("Read more")}
                      </button>
                    )}
                  </>
                ) : (
                  "-"
                )}
              </dd>
            </div>
          </dl>
        </div>

        {/* Accounts Table */}
        <div className="bg-white shadow sm:rounded-lg border border-gray-100 overflow-hidden">
          <div className="max-w-full overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {t("transactions.table.headers.account")}
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {t("transactions.table.headers.debit")}
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {t("transactions.table.headers.credit")}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {transaction.accounts?.items?.map((item, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 text-sm">
                      <div className="text-gray-900 font-medium">
                        {item.account?.code}
                      </div>
                      <div className="text-gray-500 text-xs">
                        {item.account?.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                      {item.debit > 0 ? currencyFormat(item.debit) : "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                      {item.credit > 0 ? currencyFormat(item.credit) : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </SlideOver>
  );
};

export default TransactionDetailModal;
